import { useContext, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import {
  removeStock,
  createStockRemovalRequest,
  updateStockRemovalRequestDraft,
  submitStockRemovalRequestDraft,
  uploadProductImage,
} from "../../../services/inventory";

import { ToastContext } from "../../../hooks/context";
import { cleanObject } from "../../../helpers";
import {
  useGetTrackItems,
  useGetStockRemovalRequestDetails,
} from "../../../hooks/api/queries";

export const useRemoveStock = ({ closeWidget, draftRequest = null }) => {
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const [stores, setStores] = useState(storeList || []);
  const [selectedProductVariants, setSelectedProductVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");

  const [formData, setFormData] = useState({
    product_id: "",
    variant_id: "",
    store_id: "",
    quantity: 0,
    reason: "",
    description: "",
    expiry_date: "",
    item_ids: [],
  });

  const { data: fullDraftDetails, isLoading: isLoadingDraftDetails } =
    useGetStockRemovalRequestDetails({
      id: draftRequest?.id,
      isEnabled: Boolean(draftRequest?.id),
    });

  const activeDraft = fullDraftDetails || draftRequest;
  const firstItem = activeDraft?.items?.[0];

  useEffect(() => {
    if (activeDraft && firstItem) {
      const storeId =
        activeDraft?.store?.id ||
        activeDraft?.store_id ||
        firstItem?.store_id ||
        "";

      const productId =
        firstItem?.product_id ||
        (typeof firstItem?.product === "object"
          ? firstItem?.product?.id
          : firstItem?.product) ||
        "";

      const variantId =
        firstItem?.variant_id ||
        (typeof firstItem?.variant === "object"
          ? firstItem?.variant?.id
          : firstItem?.variant) ||
        "";

      const expiry = firstItem?.expiry_date
        ? firstItem.expiry_date.split("T")[0]
        : "";

      setFormData({
        product_id: productId,
        variant_id: variantId,
        store_id: storeId,
        quantity: firstItem?.quantity || 1,
        reason: firstItem?.reason || "",
        description: firstItem?.description || activeDraft?.remarks || "",
        expiry_date: expiry,
        item_ids: firstItem?.item_ids || [],
      });

      if (Array.isArray(firstItem?.images)) {
        setImages(firstItem.images);
      }

      if (firstItem?.product?.variants?.length) {
        setSelectedProductVariants(
          firstItem.product.variants.map((v) => ({
            label: `${v.option} - ${v.value}`,
            value: v.id,
          })),
        );
      } else if (firstItem?.variant || firstItem?.variant_name) {
        const variantLabel =
          typeof firstItem?.variant === "object"
            ? firstItem.variant.option
              ? `${firstItem.variant.option} - ${firstItem.variant.value}`
              : firstItem.variant_name || "Variant"
            : firstItem?.variant_name || "Variant";
        const variantVal =
          typeof firstItem?.variant === "object"
            ? firstItem.variant.id || firstItem.variant_id
            : firstItem?.variant || firstItem?.variant_id || "";

        setSelectedProductVariants([
          {
            label: variantLabel,
            value: variantVal,
          },
        ]);
      }

      if (firstItem?.product?.stores?.length) {
        setStores(firstItem.product.stores);
      } else if (storeList?.length) {
        setStores(storeList);
      } else if (activeDraft?.store) {
        setStores([activeDraft.store]);
      }
    } else if (!draftRequest) {
      setFormData({
        product_id: "",
        variant_id: "",
        store_id: "",
        quantity: 0,
        reason: "",
        description: "",
        expiry_date: "",
        item_ids: [],
      });
      setImages([]);
      setSelectedProductVariants([]);
      if (storeList?.length) {
        setStores(storeList);
      }
    }
  }, [activeDraft, firstItem, draftRequest, storeList]);

  const currentStore =
    storeList?.find((s) => `${s?.id}` === `${formData?.store_id}`) ||
    stores?.find((s) => `${s?.id}` === `${formData?.store_id}`) ||
    activeDraft?.store;

  const requiresApproval =
    Boolean(currentStore?.require_stock_removal_approval) ||
    Boolean(activeDraft);

  const requiresEvidence = Boolean(
    currentStore?.require_stock_removal_evidence ||
    activeDraft?.store?.require_stock_removal_evidence,
  );

  const { data } = useGetTrackItems({
    otherParams: cleanObject({
      is_sold: false,
      product_id: formData?.product_id,
      variant_id: formData?.variant_id,
      store_id: formData?.store_id,
    }),
    isEnabled:
      formData?.product_id && formData?.product_id !== "" ? true : false,
  });

  const removeStockMutation = useMutation({
    mutationFn: (data) => removeStock(data),
    onSuccess: () => {
      triggerToast("Product removed successfully", "success");
      queryClient.invalidateQueries(["getProductList"]);
      queryClient.invalidateQueries(["getStockHistory"]);
      closeWidget();
    },
  });

  const [isDraftAction, setIsDraftAction] = useState(false);

  const createRequestMutation = useMutation({
    mutationFn: (data) => createStockRemovalRequest(data),
    onSuccess: (_, variables) => {
      triggerToast(
        variables?.save_as_draft
          ? "Stock removal request saved as draft"
          : "Stock removal request submitted for approval",
        "success",
      );
      queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
      queryClient.invalidateQueries(["getProductList"]);
      closeWidget();
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to create stock removal request";
      triggerToast(msg, "error");
    },
  });

  const updateDraftMutation = useMutation({
    mutationFn: ({ id, payload }) =>
      updateStockRemovalRequestDraft({ id, payload }),
  });

  const submitDraftMutation = useMutation({
    mutationFn: (id) => submitStockRemovalRequestDraft(id),
  });

  const handleUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (images.length >= 4) {
      triggerToast("Maximum of 4 images allowed", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      triggerToast("Image size cannot exceed 5MB", "error");
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    setIsUploadingImage(true);
    uploadProductImage(uploadFormData)
      .then((res) => {
        const url = res?.data?.data?.url || res?.data?.url;
        if (url) {
          setImages((prev) => [...prev, url]);
          setImageError("");
          triggerToast("Image uploaded successfully", "success");
        }
      })
      .finally(() => {
        setIsUploadingImage(false);
        e.target.value = "";
      });
  };

  const handleRemoveImage = (indexToRemove) => {
    setImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleRemoveStock = (isDraft = false) => {
    const isDraftSave = isDraft === true;
    setIsDraftAction(isDraftSave);
    if (requiresEvidence && !isDraftSave && images.length === 0) {
      setImageError("At least one image is required for this store.");
      return;
    }
    setImageError("");

    const formattedExpiry = formData?.expiry_date
      ? `${formData.expiry_date}T00:00:00Z`
      : undefined;

    const payloadItems = [
      cleanObject({
        product_id: formData.product_id,
        variant_id: formData.variant_id ? formData.variant_id : undefined,
        quantity: Number(formData.quantity),
        item_ids: formData.item_ids?.length ? formData.item_ids : undefined,
        reason: formData.reason,
        description: formData.description || undefined,
        expiry_date: formattedExpiry,
        images: images?.length ? images : undefined,
      }),
    ];

    const draftId = activeDraft?.id;

    if (draftId) {
      // Continuing an existing draft
      const updatePayload = {
        store_id: formData.store_id,
        remarks: formData.description || formData.reason,
        save_as_draft: isDraftSave,
        items: payloadItems,
      };

      if (isDraftSave) {
        // Just save/update the draft
        updateDraftMutation.mutate(
          { id: draftId, payload: updatePayload },
          {
            onSuccess: () => {
              triggerToast(
                "Stock removal draft updated successfully",
                "success",
              );
              queryClient.invalidateQueries(["getStockRemovalRequestsList"]);
              queryClient.invalidateQueries([
                "getStockRemovalRequestDetails",
                `${draftId}`,
              ]);
              queryClient.invalidateQueries(["getStockRemovalRequestDetails"]);
              queryClient.invalidateQueries(["getProductList"]);
              closeWidget();
            },
            onError: (err) => {
              const msg =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "Failed to update draft";
              triggerToast(msg, "error");
            },
          },
        );
      } else {
        // Submit for approval: first update draft with current form changes, then submit
        updateDraftMutation.mutate(
          { id: draftId, payload: updatePayload },
          {
            onSuccess: () => {
              submitDraftMutation.mutate(draftId, {
                onSuccess: () => {
                  triggerToast(
                    "Stock removal request submitted for approval",
                    "success",
                  );
                  queryClient.invalidateQueries([
                    "getStockRemovalRequestsList",
                  ]);
                  queryClient.invalidateQueries([
                    "getStockRemovalRequestDetails",
                    `${draftId}`,
                  ]);
                  queryClient.invalidateQueries([
                    "getStockRemovalRequestDetails",
                  ]);
                  queryClient.invalidateQueries(["getProductList"]);
                  closeWidget();
                },
                onError: (err) => {
                  const msg =
                    err?.response?.data?.message ||
                    err?.response?.data?.detail ||
                    "Failed to submit draft for approval";
                  triggerToast(msg, "error");
                },
              });
            },
            onError: (err) => {
              const msg =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "Failed to update draft before submitting";
              triggerToast(msg, "error");
            },
          },
        );
      }
      return;
    }

    // Creating a fresh request
    if (requiresApproval) {
      createRequestMutation.mutate({
        store_id: formData.store_id,
        remarks: formData.description || formData.reason,
        save_as_draft: isDraftSave,
        items: payloadItems,
      });
    } else {
      removeStockMutation.mutate(
        cleanObject({
          ...formData,
          images: images?.length ? images : undefined,
        }),
      );
    }
  };

  return {
    formData,
    isDeletingStock:
      removeStockMutation?.isLoading ||
      createRequestMutation?.isLoading ||
      updateDraftMutation?.isLoading ||
      submitDraftMutation?.isLoading ||
      isUploadingImage,
    isDraftAction,
    stores,
    selectedProductVariants,
    images,
    isUploadingImage,
    imageError,
    requiresApproval,
    requiresEvidence,
    activeDraft,
    firstItem,
    isLoadingDraftDetails,
    handleUploadImage,
    handleRemoveImage,
    setStores,
    setFormData,
    setSelectedProductVariants,
    trackedItems: data?.data || [],
    handleRemoveStock,
  };
};
